import React, { PropsWithChildren, useContext } from 'react';

import { useGetClientToken } from 'hooks/useGetClientToken';
import LoadingFullscreen from 'components/common/loading/LoadingFullscreen';

export interface AuthContextValue {
  clientToken?: string;
  refetch?: () => any;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export function AuthContextProvider(props: PropsWithChildren) {
  const {
    data: clientToken,
    isSuccess,
    refetch,
  } = useGetClientToken({
    refetchInterval: (data) => {
      // if data returned from last fetch;
      // extract the `expires_in` (seconds) value
      // refetch at 90% of the expiration time
      return data?.expires_in ? 1000 * (data.expires_in * 0.9) : false;
    },
    refetchIntervalInBackground: true,
  });

  const value = {
    clientToken: clientToken?.access_token,
    refetch,
  };

  const expiresAt = (clientToken?.expires_in || 0) * 1000 + Date.now();
  const isExpired = expiresAt < Date.now();
  const isReady = isSuccess && !isExpired;

  return (
    <AuthContext.Provider value={value}>
      {!isReady && (
        <div className="h-screen" data-testid="provider-auth-loading">
          <LoadingFullscreen />
        </div>
      )}
      {isReady && <>{props.children}</>}
    </AuthContext.Provider>
  );
}

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext hook must be used within an AuthContextProvider');
  }

  return context;
};
